import {
	AUTO_OPTION_VALUE,
	buildMatchOptions,
	buildRateOptions,
	buildWorthOptions,
	matchOptions,
	normalizeWorthMatch,
	normalizeWorthMatchInput,
	normalizeWorthScore,
	normalizeWorthValue,
	rateOptions,
	withAutoLabel,
	worthOptions,
} from 'src/components/ui/DefaultForm/hooks/worthOptions';
import { mapSalesTargetFormValues } from 'src/utils/mappers/companySalesTarget.mapper';

const findOption = (options, value) =>
	options.find((option) => option.value === value) ?? null;

const resolveOption = (options, manual, auto, normalize) => {
	const normalizedManual = normalize(manual);
	const normalizedAuto = normalize(auto);

	if (normalizedManual !== null) {
		return findOption(options, normalizedManual);
	}

	if (normalizedAuto !== null) {
		return withAutoLabel(findOption(options, normalizedAuto));
	}

	return null;
};

const resolveMatchOption = (manual) => {
	const normalized = normalizeWorthMatch(manual);

	if (normalized === null) {
		return findOption(matchOptions, 'UNKNOWN');
	}

	return findOption(matchOptions, normalized);
};

export const mapWorthToFormValues = (worth = {}, company) => {
	const values = {
		worth: resolveOption(
			worthOptions,
			worth.value,
			worth.valueAuto,
			normalizeWorthValue,
		),
		wedding: resolveOption(
			rateOptions,
			worth.wedding,
			worth.weddingAuto,
			normalizeWorthScore,
		),
		birthday: resolveOption(
			rateOptions,
			worth.birthday,
			worth.birthdayAuto,
			normalizeWorthScore,
		),
		corporate: resolveMatchOption(worth.corporative),
		newYear: resolveOption(
			rateOptions,
			worth.newYear,
			worth.newYearAuto,
			normalizeWorthScore,
		),
		graduation: resolveMatchOption(worth.graduation),
	};

	if (company) {
		return {
			...values,
			...mapSalesTargetFormValues(company),
		};
	}

	return values;
};

export const buildWorthFormOptions = (worth = {}) => ({
	worth: buildWorthOptions(worth.valueAuto),
	wedding: buildRateOptions(worth.weddingAuto),
	birthday: buildRateOptions(worth.birthdayAuto),
	corporate: buildMatchOptions(),
	newYear: buildRateOptions(worth.newYearAuto),
	graduation: buildMatchOptions(),
});

const isAutoField = (field) =>
	Boolean(field?.isAuto) || field?.value === AUTO_OPTION_VALUE;

const resolveWorthValueField = (field) => {
	if (isAutoField(field)) {
		return null;
	}

	return normalizeWorthValue(field?.value);
};

const resolveWorthScoreField = (field) => {
	if (isAutoField(field)) {
		return null;
	}

	return normalizeWorthScore(field?.value);
};

const resolveWorthMatchField = (field) => {
	if (isAutoField(field)) {
		return null;
	}

	return normalizeWorthMatchInput(field?.value);
};

export const mapFormValuesToWorthInput = (values) => ({
	value: resolveWorthValueField(values.worth),
	wedding: resolveWorthScoreField(values.wedding),
	birthday: resolveWorthScoreField(values.birthday),
	corporative: resolveWorthMatchField(values.corporate),
	newYear: resolveWorthScoreField(values.newYear),
	graduation: resolveWorthMatchField(values.graduation),
});

export const hasWorthFormChanges = (values, worth = {}) => {
	const next = mapFormValuesToWorthInput(values);
	const current = {
		value: normalizeWorthValue(worth.value),
		wedding: normalizeWorthScore(worth.wedding),
		birthday: normalizeWorthScore(worth.birthday),
		corporative: normalizeWorthMatchInput(worth.corporative),
		newYear: normalizeWorthScore(worth.newYear),
		graduation: normalizeWorthMatchInput(worth.graduation),
	};

	return (
		next.value !== current.value ||
		next.wedding !== current.wedding ||
		next.birthday !== current.birthday ||
		next.corporative !== current.corporative ||
		next.newYear !== current.newYear ||
		next.graduation !== current.graduation
	);
};
